// 會員文書共用的步驟控制 hook。
// 職責：管理目前步驟、收集當前步驟資料，以及判斷是否允許換頁。
// 不負責表單資料的保存方式與畫面呈現。
import { useState, useRef, useLayoutEffect } from "react";
import {
	isMembershipSurveyCompleted,
	canAccessMembershipStep,
} from "@icoach/documents/membership/membershipSurveyRules";

/**
 * @param {number}   stepCount        總步數
 * @param {number}   surveyStepIndex  「會員須知」是第幾步（入會為 1，移入為 2）
 * @param {boolean}  isReadOnly       是否唯讀檢視
 * @param {Function} readSurveyState 收集當前步驟後，讀取最新的會員須知判斷資料
 * @param {Function} onCollect        收集到當前步驟資料時呼叫，由呼叫端自行保存
 * @param {Function} onBlocked        換頁被擋下時呼叫，由父層決定要不要跳提示
 */
const useMembershipStepper = ({
	stepCount,
	surveyStepIndex,
	isReadOnly = false,
	readSurveyState,
	onCollect,
	onBlocked,
}) => {
	// 卡控從「會員須知的下一步」開始 —— 依定義如此，故不另外開參數，避免兩個值不同步。
	const guardedFromIndex = surveyStepIndex + 1;

	const [currentStep, setCurrentStep] = useState(0);

	// 指向「當前這一步的元件」；欄位的 ref 由各步驟元件自己持有。
	const contentRef = useRef(null);
	// 初次載入不捲動，只有真的換頁才捲。
	const isInitRef = useRef(true);

	// 取得當前步驟的即時資料（直接問畫面，不讀呼叫端保存的資料）
	const getCurrentStepResult = () => contentRef.current?.getResult?.() ?? {};

	const goToStep = nextIndex => {
		if (nextIndex < 0 || nextIndex >= stepCount) return;

		// 先收集再換頁：漏掉這段會造成「切走再切回來，剛填的東西不見了」。
		const stepResult = getCurrentStepResult();
		if (typeof onCollect === "function") onCollect(stepResult);

		// 收集完成後才讀取判斷資料，確保 ref 或 state 都能提供最新值。
		const surveyState =
			typeof readSurveyState === "function"
				? readSurveyState(stepResult)
				: { isNoticeReady: false };
		const isCompleted = isMembershipSurveyCompleted(surveyState);
		const canGoToStep = canAccessMembershipStep({
			nextIndex,
			guardedFromIndex,
			isReadOnly,
			isCompleted,
		});

		if (!canGoToStep) {
			// 人就在會員須知那頁時，順便讓缺漏欄位顯示紅字，教練才知道要補什麼。
			// 寫成完整的 if 而非 contentRef.current?.isError?.()：ESLint 的 no-unused-expressions
			// 會把「單獨成一行的可選呼叫」當成沒有作用的運算式而報錯。
			if (currentStep === surveyStepIndex && contentRef.current?.isError) {
				contentRef.current.isError();
			}
			if (typeof onBlocked === "function") onBlocked();
			return;
		}

		setCurrentStep(nextIndex);
	};

	// 換頁後捲回頁面最上方；初次載入不捲，否則一進申請書就會被強制捲動一次。
	useLayoutEffect(
		() => {
			if (typeof window === "undefined") return;
			if (isInitRef.current) {
				isInitRef.current = false;
				return;
			}
			window.scrollTo({ top: 0, behavior: "smooth" });
		},
		[currentStep],
	);

	return {
		currentStep,
		contentRef,
		goToStep,
		getCurrentStepResult,
	};
};

export default useMembershipStepper;
